import { createContext, useContext, useState, useEffect } from 'react';
import { axiosInstance } from '@/lib/axios';
import i18n from '@/i18n';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user from token on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axiosInstance.get('/auth/me')
        .then(response => {
          setUser(response.data);
          // Apply saved language preference
          if (response.data?.language) {
            i18n.changeLanguage(response.data.language);
            localStorage.setItem('language', response.data.language);
          }
        })
        .catch((error) => {
          // Only a rejected token means the session is gone. Any other failure (rate limit,
          // network blip, server hiccup) used to silently sign the user out.
          if (error?.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = (userData, token) => {
    return new Promise((resolve) => {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      // Apply saved language preference on login
      if (userData?.language) {
        i18n.changeLanguage(userData.language);
        localStorage.setItem('language', userData.language);
      }
      // Wait for next tick to ensure state update completes
      setTimeout(() => resolve(userData), 0);
    });
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
